/**
 * Data Processing Agreement (DPA Registry) Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class DataProcessingAgreementService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(() => this.api.getAll(params), { maxRetries: 2 });
            return {
                success: true,
                data: response.data,
                pagination: response.pagination,
                uniqueValues: response.uniqueValues,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch DPA list');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(() => this.api.getById(id), { maxRetries: 2 });
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch DPA');
        }
    }

    async create(data) {
        try {
            const response = await this.api.create(data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to create DPA');
        }
    }

    async update(id, data) {
        try {
            const response = await this.api.update(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update DPA');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete DPA');
        }
    }

    async getByVendor(vendorId) {
        try {
            const response = await this.api.getByVendor(vendorId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch vendor DPA list');
        }
    }

    async addSubProcessor(id, data) {
        try {
            const response = await this.api.addSubProcessor(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to add sub-processor');
        }
    }

    async decideSubProcessor(id, subId, data) {
        try {
            const response = await this.api.decideSubProcessor(id, subId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to decide sub-processor');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
